import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertTriangle } from "lucide-react"
import { getToolDisplayName, getScanTypeDisplayName } from "@/utils/displayNames"
import { formatDateTime } from "@/utils/dateUtils"
import { useState, useEffect } from "react"
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { SOC2ComplianceSection } from './SOC2ComplianceSection'
import type { ReportData } from '@/hooks/useReportActions'

interface ReportPreviewProps {
  reportData: ReportData
  onClose: () => void
  onDownload: () => void
}

export function ReportPreview({ reportData, onClose, onDownload }: ReportPreviewProps) {
  const [expandedVulns, setExpandedVulns] = useState<Set<string>>(new Set())

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const toggleExpanded = (vulnId: string) => {
    setExpandedVulns(prev => {
      const newSet = new Set(prev)
      if (newSet.has(vulnId)) {
        newSet.delete(vulnId)
      } else {
        newSet.add(vulnId)
      }
      return newSet
    })
  }

  const parseEvidence = (evidence: string | Array<any> | undefined) => {
    if (!evidence) return []

    if (typeof evidence === 'string') {
      try {
        const parsed = JSON.parse(evidence)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }

    return Array.isArray(evidence) ? evidence : []
  }

  // Markdown components for rendering recommendations
  const markdownComponents = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '')
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          className="rounded-md my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
          {children}
        </code>
      )
    },
    p({ children }: any) {
      return <p className="mb-2">{children}</p>
    },
    strong({ children }: any) {
      return <strong className="font-semibold">{children}</strong>
    },
    a({ href, children }: any) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
        >
          {children}
        </a>
      )
    }
  }

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "high":
        return <Badge className="bg-red-100 text-red-800">High</Badge>
      case "medium":
        return <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>
      case "low":
        return <Badge className="bg-blue-100 text-blue-800">Low</Badge>
      case "info":
      case "informational":
        return <Badge className="bg-gray-100 text-gray-800">Info</Badge>
      default:
        return <Badge variant="outline">{severity}</Badge>
    }
  }

  const getTimeEstimateBadge = (timeEstimate: string) => {
    return <Badge className="bg-purple-100 text-purple-800 ml-2 whitespace-nowrap">{timeEstimate}</Badge>
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-green-600"
    if (score >= 70) return "text-yellow-600"
    return "text-red-600"
  }

  const sortVulnerabilitiesBySeverity = (vulnerabilities: typeof reportData.vulnerabilities) => {
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4, informational: 4 }
    return [...vulnerabilities].sort((a, b) => {
      const orderA = severityOrder[a.severity as keyof typeof severityOrder] ?? 5
      const orderB = severityOrder[b.severity as keyof typeof severityOrder] ?? 5
      return orderA - orderB
    })
  }

  const sortedVulnerabilities = sortVulnerabilitiesBySeverity(reportData.vulnerabilities)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Mobile-optimized header */}
        <div className="flex flex-col gap-3 p-4 sm:p-6 border-b">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold truncate">Security Report Preview</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">{reportData.target}</p>
            </div>
            {/* Close button - always visible on mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex-shrink-0 sm:hidden"
            >
              ✕
            </Button>
          </div>
          {/* Buttons row */}
          <div className="flex gap-2">
            <Button onClick={onDownload} className="flex-1 sm:flex-none">
              Download PDF
            </Button>
            <Button variant="outline" onClick={onClose} className="hidden sm:inline-flex">
              Close
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 sm:p-6">
          {/* Warning Banner for Partially Completed Scans */}
          {reportData.warnings && reportData.warnings.length > 0 && (
            <Alert variant="destructive" className="mb-4 sm:mb-6 border-yellow-400 bg-yellow-50">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <AlertTitle className="text-yellow-800 font-semibold">
                Incomplete Scan - Some Tools Failed
              </AlertTitle>
              <AlertDescription className="text-yellow-700">
                <p className="mb-2">
                  {reportData.scan_completeness || `${reportData.warnings.length} tool(s) encountered issues`}
                </p>
                <div className="mt-3 space-y-2">
                  {reportData.warnings.map((warning, idx) => (
                    <div key={idx} className="bg-yellow-100 border-l-4 border-yellow-500 p-3 rounded">
                      <p className="font-medium text-yellow-900">
                        {warning.tool.toUpperCase()}: {warning.message}
                      </p>
                      <p className="text-sm text-yellow-800 mt-1">
                        <strong>Impact:</strong> {warning.impact}
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {warning.recommendation}
                      </p>
                    </div>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className={`grid w-full mb-4 sm:mb-6 ${reportData.soc2Compliance ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
              <TabsTrigger value="vulnerabilities" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Vulnerabilities</span>
                <span className="sm:hidden">Issues</span>
              </TabsTrigger>
              {reportData.soc2Compliance && (
                <TabsTrigger value="soc2" className="text-xs sm:text-sm">SOC2</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="w-full">
              <div className="space-y-4 sm:space-y-6 w-full">
                {/* Executive Summary */}
                <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">Executive Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-700">Security Score</h4>
                    <p className={`text-2xl font-bold ${getScoreColor(reportData.securityScore)}`}>
                      {reportData.securityScore}/100
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Total Issues</h4>
                    <p className="text-2xl font-bold text-gray-900">
                      {reportData.vulnerabilities.length}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 text-sm sm:text-base">Severity Breakdown</h4>
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    <div className="flex items-center gap-1 sm:gap-2">
                      {getSeverityBadge("high")}
                      <span className="text-xs sm:text-sm">
                        {reportData.vulnerabilities.filter(v => v.severity === "high").length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {getSeverityBadge("medium")}
                      <span className="text-xs sm:text-sm">
                        {reportData.vulnerabilities.filter(v => v.severity === "medium").length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {getSeverityBadge("low")}
                      <span className="text-xs sm:text-sm">
                        {reportData.vulnerabilities.filter(v => v.severity === "low").length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {getSeverityBadge("info")}
                      <span className="text-xs sm:text-sm">
                        {reportData.vulnerabilities.filter(v => v.severity === "info" || v.severity === "informational").length}
                      </span>
                    </div>
                  </div>
                </div>

                {reportData.vulnerabilities.length > 0 && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-3 mt-4">
                    <p className="text-blue-800 text-sm">
                      After fixing these {reportData.vulnerabilities.length} issue{reportData.vulnerabilities.length !== 1 ? 's' : ''}, re-scan to verify fixes and track your security score improvement.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scan Details */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">Scan Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <p className="text-gray-600">Scan Type:</p>
                    <p className="font-medium">{getScanTypeDisplayName(reportData.scanType)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Duration:</p>
                    <p className="font-medium">{reportData.scanDetails.duration}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Start Time:</p>
                    <p className="font-medium">{formatDateTime(reportData.scanDetails.startTime)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">End Time:</p>
                    <p className="font-medium">{formatDateTime(reportData.scanDetails.endTime)}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-gray-600 text-sm">Tools Used:</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {reportData.scanDetails.toolsUsed.map((tool, index) => (
                      <Badge key={index} variant="outline">{getToolDisplayName(tool)}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
            </TabsContent>

            <TabsContent value="vulnerabilities" className="w-full">
              {/* Vulnerabilities */}
              <Card>
                <CardHeader>
                  <CardTitle>Security Issues</CardTitle>
                  <CardDescription>
                    Detailed findings from the security scan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {reportData.vulnerabilities.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-green-600 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-green-600">No Issues Found</h3>
                      <p className="text-gray-500">This scan found no security vulnerabilities.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedVulnerabilities.map((vuln) => {
                        const evidenceList = parseEvidence(vuln.evidence)
                        const isGrouped = evidenceList.length > 0
                        const isExpanded = expandedVulns.has(vuln.id)
                        const soc2Controls = (vuln as any).soc2_controls || []

                        return (
                          <div key={vuln.id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{vuln.title}</h4>
                                {/* SOC2 Control Badges */}
                                {soc2Controls.length > 0 && (
                                  <div className="flex gap-1 mt-2 flex-wrap">
                                    {soc2Controls.map((control: string) => (
                                      <Badge key={control} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        SOC2: {control}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center ml-2">
                                {getSeverityBadge(vuln.severity)}
                                {(vuln as any).remediationTime && getTimeEstimateBadge((vuln as any).remediationTime)}
                              </div>
                            </div>
                            <p className="text-gray-700 mb-3 text-sm">{vuln.description}</p>

                            {/* Show expandable instances if this is a grouped vulnerability */}
                            {isGrouped && (
                              <div className="mb-3">
                                <button
                                  onClick={() => toggleExpanded(vuln.id)}
                                  className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <svg
                                    className={`w-4 h-4 mr-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  {isExpanded ? 'Hide' : 'Show'} {evidenceList.length} instances
                                </button>

                                {isExpanded && (
                                  <div className="mt-3 pl-5 border-l-2 border-gray-200">
                                    <div className="space-y-3">
                                      {evidenceList.map((instance, index) => (
                                        <div key={index} className="bg-gray-50 p-3 rounded text-sm">
                                          <div className="font-medium text-gray-700 mb-1">
                                            {instance.location || `Instance ${index + 1}`}
                                          </div>
                                          {instance.description && (
                                            <div className="text-gray-600 mb-2">{instance.description}</div>
                                          )}
                                          {instance.evidence && (
                                            <div className="bg-blue-700 border border-blue-500 p-2 rounded font-mono text-xs overflow-x-auto">
                                              <code className="text-blue-50">{instance.evidence}</code>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                              <h5 className="font-medium text-blue-800 mb-2">Recommendation:</h5>
                              <div className="text-blue-700 text-sm prose prose-sm max-w-none prose-pre:bg-transparent prose-pre:p-0">
                                <ReactMarkdown components={markdownComponents}>
                                  {vuln.recommendation}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SOC2 Compliance Tab */}
            {reportData.soc2Compliance && (
              <TabsContent value="soc2" className="w-full">
                <SOC2ComplianceSection soc2Compliance={reportData.soc2Compliance} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  )
}